/* PURE LAB — the custom elements the theme's markup relies on.
   Written for this store; replaces the compiled bundle the prototype inherited. */
(function () {
  'use strict';

  var define = function (name, constructor) {
    if (!window.customElements.get(name)) window.customElements.define(name, constructor);
  };

  var media = function (query) {
    return window.matchMedia(query).matches;
  };

  var prefersReducedMotion = function () {
    return media('(prefers-reduced-motion: reduce)');
  };

  /* ================================================================ dialogs
     x-drawer, x-drawer-popover, x-popover and the menu drawer share one base:
     a shadow root cloned from a <template>, opened by any [aria-controls] that
     points at the element's id. */
  var openDialogs = [];

  var DialogElement = class extends HTMLElement {
    static get observedAttributes() {
      return ['open'];
    }

    connectedCallback() {
      if (this.initialised) return;

      if (this.usesShadowDom) {
        var template = this.querySelector(':scope > template') || document.getElementById(this.templateId);
        if (!template) return;

        this.attachShadow({ mode: 'open' });
        this.shadowRoot.append(template.content.cloneNode(true));
        this.shadowRoot.addEventListener('click', this.onShadowClick.bind(this));
      }

      this.initialised = true;
      this.hidden = true;
      this.addEventListener('click', this.onLightDomClick.bind(this));

      this.triggers = Array.from(document.querySelectorAll('[aria-controls="' + this.id + '"]'));
      this.triggers.forEach(
        function (trigger) {
          trigger.addEventListener('click', this.onTriggerClick.bind(this, trigger));
          if (trigger.hasAttribute('data-open-on-hover')) {
            trigger.addEventListener('pointerenter', this.onTriggerPointerEnter.bind(this));
          }
        }.bind(this)
      );
    }

    get templateId() {
      return 'drawer-default-template';
    }

    get usesShadowDom() {
      return true;
    }

    get open() {
      return this.hasAttribute('open');
    }

    onTriggerClick(trigger, event) {
      event.preventDefault();
      this.lastTrigger = trigger;
      if (this.open) {
        this.hide();
      } else {
        this.show();
      }
    }

    onTriggerPointerEnter() {
      if (this.open) return;
      this.closeOnPointerElsewhere = true;
      this.show();
    }

    /* Closing on the panel's own pointerleave is too eager: the link and the panel are
       separated by the header's padding, and crossing that gap already counts as
       leaving. Watch instead for the pointer arriving somewhere unrelated — anything
       that is neither the panel, nor inside it, nor an ancestor it sits in. */
    onPointerEnterElsewhere(event) {
      var target = event.target;
      if (this.contains(target) || (target.contains && target.contains(this))) return;
      if (target.getAttribute && target.getAttribute('aria-controls') === this.id) return;
      this.hide();
    }

    onLightDomClick(event) {
      if (event.target.closest('[data-dialog-close]')) this.hide();
    }

    onShadowClick(event) {
      var part = event.target.closest('[part~="overlay"]');
      if (part) this.hide();
    }

    show() {
      if (this.open) return;

      this.hidden = false;
      /* Reading a layout property flushes the hidden state, so the transition
         still runs — and unlike rAF this also works in a background tab. */
      void this.offsetHeight;
      this.setAttribute('open', '');

      this.triggers.forEach(function (trigger) {
        trigger.setAttribute('aria-expanded', 'true');
      });

      if (this.lockScroll) document.documentElement.style.setProperty('overflow', 'hidden');
      openDialogs.push(this);

      if (this.closeOnPointerElsewhere && !this.pointerWatcher) {
        this.pointerWatcher = this.onPointerEnterElsewhere.bind(this);
        document.addEventListener('pointerenter', this.pointerWatcher, true);
      }

      this.dispatchEvent(new CustomEvent('dialog:after-show', { bubbles: true }));

      /* A panel that opened under the pointer leaves focus alone: moving it would draw a
         focus ring around the first link, which is not something anybody asked for by
         hovering. Panels opened by click or key still hand focus over. */
      if (this.closeOnPointerElsewhere) return;

      window.setTimeout(
        function () {
          var focusable = this.querySelector('input:not([type="hidden"]), button, a[href], select, textarea');
          /* preventScroll: a panel in the header would otherwise drag the page to it. */
          if (focusable) focusable.focus({ preventScroll: true });
        }.bind(this),
        150
      );
    }

    hide() {
      if (!this.open) return;

      this.removeAttribute('open');
      this.triggers.forEach(function (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
      });

      if (this.pointerWatcher) {
        document.removeEventListener('pointerenter', this.pointerWatcher, true);
        this.pointerWatcher = null;
        this.closeOnPointerElsewhere = false;
      }

      openDialogs = openDialogs.filter(
        function (dialog) {
          return dialog !== this;
        }.bind(this)
      );

      if (this.lockScroll && openDialogs.length === 0) {
        document.documentElement.style.removeProperty('overflow');
      }

      var finish = function () {
        if (!this.open) this.hidden = true;
      }.bind(this);

      if (prefersReducedMotion()) {
        finish();
      } else {
        window.setTimeout(finish, 350);
      }

      this.dispatchEvent(new CustomEvent('dialog:after-hide', { bubbles: true }));
    }

    get lockScroll() {
      return true;
    }
  };

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape' || openDialogs.length === 0) return;
    openDialogs[openDialogs.length - 1].hide();
  });

  define('x-drawer', class extends DialogElement {});
  define('cart-drawer', class extends DialogElement {});

  define(
    'x-drawer-popover',
    class extends DialogElement {
      get templateId() {
        return 'drawer-popover-default-template';
      }
    }
  );

  define(
    'x-modal',
    class extends DialogElement {
      get templateId() {
        return 'modal-default-template';
      }
    }
  );

  define(
    'x-popover',
    class extends DialogElement {
      get templateId() {
        return 'popover-default-template';
      }

      get lockScroll() {
        return false;
      }

      show() {
        super.show();
        /* Popovers open above their trigger when asked to, and otherwise below. */
        this.classList.toggle('is-above', this.getAttribute('preferred-position') === 'above');
        this.classList.toggle('is-aligned-start', this.getAttribute('preferred-alignment') === 'start');

        /* A popover draws no veil over the page, so nothing was catching the taps that
           should dismiss it: it could only be closed from the control that opened it.
           Scrolling closes it too, since it would otherwise drift away from its
           trigger. */
        if (!this.dismiss) {
          this.dismiss = function (event) {
            if (event.type !== 'scroll') {
              if (this.contains(event.target)) return;
              if (event.target.closest && event.target.closest('[aria-controls="' + this.id + '"]')) return;
            }
            this.hide();
          }.bind(this);
        }

        document.addEventListener('pointerdown', this.dismiss, true);
        window.addEventListener('scroll', this.dismiss, { passive: true });
      }

      hide() {
        if (this.dismiss) {
          document.removeEventListener('pointerdown', this.dismiss, true);
          window.removeEventListener('scroll', this.dismiss);
        }
        super.hide();
      }
    }
  );

  define(
    'dialog-close-button',
    class extends HTMLElement {
      connectedCallback() {
        this.addEventListener('click', function (event) {
          var root = event.target.getRootNode();
          var dialog = root.host || event.target.closest('x-drawer, x-popover, x-drawer-popover, x-modal, cart-drawer, header-menu-drawer');
          if (dialog && typeof dialog.hide === 'function') dialog.hide();
        });
      }
    }
  );

  /* ================================================================= header */
  define(
    'height-observer',
    class extends HTMLElement {
      connectedCallback() {
        this.variable = this.getAttribute('variable');
        /* The host is display:contents, so the wrapped element carries the height. */
        this.target = this.firstElementChild || this;
        this.observer = new ResizeObserver(this.measure.bind(this));
        this.observer.observe(this.target);
        this.measure();
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      measure() {
        document.documentElement.style.setProperty(
          '--' + this.variable + '-height',
          Math.round(this.target.getBoundingClientRect().height) + 'px'
        );
      }
    }
  );

  define(
    'x-header',
    class extends HTMLElement {
      connectedCallback() {
        /* Panels that hang under the header need to know where its bottom edge is right
           now: with the announcement bar still on screen that is lower than the header's
           own height, and it rises as the bar scrolls away. */
        this.publishBottom = function () {
          if (this.pending) return;
          this.pending = window.requestAnimationFrame(
            function () {
              this.pending = null;
              document.documentElement.style.setProperty(
                '--header-bottom',
                Math.max(0, Math.round(this.getBoundingClientRect().bottom)) + 'px'
              );
            }.bind(this)
          );
        }.bind(this);

        this.publishBottom();
        window.addEventListener('scroll', this.publishBottom, { passive: true });
        window.addEventListener('resize', this.publishBottom);

        this.tracker = document.getElementById('header-scroll-tracker');
        if (!this.tracker) return;

        this.observer = new IntersectionObserver(
          function (entries) {
            document.documentElement.classList.toggle('header-is-scrolled', !entries[0].isIntersecting);
          },
          { threshold: 0 }
        );
        this.observer.observe(this.tracker);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.publishBottom) {
          window.removeEventListener('scroll', this.publishBottom);
          window.removeEventListener('resize', this.publishBottom);
        }
      }
    }
  );

  /* The header panels stay plain elements. Cloning the drawer template would wrap them
     in its panel — a close button and a single column — and the mega menu's own grid,
     links beside the promo tiles, would never get a chance to lay them out. */
  var MenuPanel = class extends DialogElement {
    get usesShadowDom() {
      return false;
    }

    /* No scroll lock either: hiding the scrollbar reflows the page under the pointer,
       which closes a hover menu the moment it opens. The reference keeps the page
       scrollable while its menus are down. */
    get lockScroll() {
      return false;
    }
  };

  define('header-mega-menu', class extends MenuPanel {});
  define('header-dropdown-menu', class extends MenuPanel {});

  define(
    'header-menu-drawer',
    class extends DialogElement {
      show() {
        super.show();
        this.measureOffset();
      }

      /* The panel hangs below the header, so how much room it has depends on where the
         header currently sits — under the announcement bar at the top of the page, at
         the very top once that has scrolled away. */
      measureOffset() {
        var content = this.shadowRoot && this.shadowRoot.querySelector('[part~="content"]');
        if (!content) return;
        this.style.setProperty('--menu-offset-top', content.getBoundingClientRect().top + 'px');
      }

      connectedCallback() {
        super.connectedCallback();

        window.addEventListener('scroll', function () {
          if (this.open) this.measureOffset();
        }.bind(this), { passive: true });

        window.addEventListener('resize', function () {
          if (this.open) this.measureOffset();
        }.bind(this));

        this.addEventListener('click', function (event) {
          var toggle = event.target.closest('[data-panel-toggle]');
          if (!toggle) return;

          var target = document.getElementById(toggle.getAttribute('aria-controls'));
          if (!target) return;

          this.querySelectorAll('header-menu-drawer-panel').forEach(function (panel) {
            panel.classList.toggle('is-active', panel === target && !panel.hasAttribute('data-main-panel'));
            if (panel.hasAttribute('data-main-panel')) {
              panel.classList.toggle('is-behind', panel !== target);
            }
          });
        });
      }
    }
  );

  define('header-menu-drawer-panel', class extends HTMLElement {});

  /* ============================================================== carousels */
  var ScrollCarousel = class extends HTMLElement {
    connectedCallback() {
      this.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
      this.resizeObserver = new ResizeObserver(this.refresh.bind(this));
      this.resizeObserver.observe(this);
      this.selectedIndex = 0;
      this.refresh();
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    get cells() {
      return Array.from(this.children);
    }

    get selectedCell() {
      return this.cells[this.selectedIndex];
    }

    refresh() {
      this.classList.toggle('is-scrollable', this.scrollWidth > this.clientWidth + 2);
    }

    onScroll() {
      window.clearTimeout(this.scrollTimer);
      this.scrollTimer = window.setTimeout(this.settle.bind(this), 80);

      var index = this.indexAtScroll();
      if (index === this.selectedIndex) return;

      this.selectedIndex = index;
      this.dispatchEvent(new CustomEvent('carousel:change', { bubbles: true, detail: { index: index } }));
    }

    settle() {
      this.dispatchEvent(new CustomEvent('carousel:settle', { bubbles: true, detail: { index: this.selectedIndex } }));
    }

    /* The cell the carousel is parked on is the one at its leading edge, which is where
       select() scrolls to. Measuring from the middle instead made the index run ahead of
       the scroll: two taps of "next" jumped four cells and "previous" then scrolled to
       where the carousel already stood, so the back arrow looked dead. */
    indexAtScroll() {
      var cells = this.cells;
      var start = this.scrollLeft;
      var origin = cells.length ? cells[0].offsetLeft : 0;
      var best = 0;
      var bestDistance = Infinity;

      cells.forEach(function (cell, index) {
        var distance = Math.abs(cell.offsetLeft - origin - start);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = index;
        }
      });

      return best;
    }

    select(index) {
      var cells = this.cells;
      var clamped = Math.max(0, Math.min(index, cells.length - 1));
      var cell = cells[clamped];
      if (!cell) return;

      this.selectedIndex = clamped;
      /* measured from the first cell, so it does not matter which element the cells'
         offsets are counted from */
      this.scrollTo({
        left: cell.offsetLeft - cells[0].offsetLeft,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
      this.dispatchEvent(new CustomEvent('carousel:change', { bubbles: true, detail: { index: clamped } }));
    }

    next() {
      this.select(this.selectedIndex + 1);
    }

    previous() {
      this.select(this.selectedIndex - 1);
    }
  };

  define('scroll-carousel', ScrollCarousel);
  define('product-list', class extends ScrollCarousel {});
  define('trust-icons-carousel', class extends ScrollCarousel {});

  var carouselFor = function (element) {
    return document.getElementById(element.getAttribute('aria-controls'));
  };

  define(
    'carousel-prev-button',
    class extends HTMLElement {
      connectedCallback() {
        this.carousel = carouselFor(this);
        this.button = this.querySelector('button');
        if (!this.carousel || !this.button) return;

        this.button.addEventListener(
          'click',
          function () {
            this.carousel.previous();
          }.bind(this)
        );

        this.carousel.addEventListener('carousel:change', this.sync.bind(this));
        this.sync();
      }

      sync() {
        this.button.disabled = this.carousel.selectedIndex <= 0;
      }
    }
  );

  define(
    'carousel-next-button',
    class extends HTMLElement {
      connectedCallback() {
        this.carousel = carouselFor(this);
        this.button = this.querySelector('button');
        if (!this.carousel || !this.button) return;

        this.button.addEventListener(
          'click',
          function () {
            this.carousel.next();
          }.bind(this)
        );

        this.carousel.addEventListener('carousel:change', this.sync.bind(this));
        this.sync();
      }

      sync() {
        this.button.disabled = this.carousel.selectedIndex >= this.carousel.cells.length - 1;
      }
    }
  );

  define(
    'carousel-navigation',
    class extends HTMLElement {
      connectedCallback() {
        this.carousel = carouselFor(this);
        if (!this.carousel) return;

        this.buttons = Array.from(this.querySelectorAll('button'));
        this.buttons.forEach(
          function (button, index) {
            button.addEventListener(
              'click',
              function () {
                this.carousel.select(index);
              }.bind(this)
            );
          }.bind(this)
        );

        this.carousel.addEventListener('carousel:change', this.sync.bind(this));
        this.sync();
      }

      sync() {
        this.buttons.forEach(
          function (button, index) {
            button.setAttribute('aria-current', String(index === this.carousel.selectedIndex));
          }.bind(this)
        );
      }
    }
  );

  /* Fade carousels: one cell visible at a time, driven by a class. */
  var FadeCarousel = class extends HTMLElement {
    connectedCallback() {
      this.selectedIndex = 0;
      this.cellSelector = this.getAttribute('cell-selector');
      this.startAutoplay();
      this.bindSwipe();
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    get cells() {
      return this.cellSelector
        ? Array.from(this.querySelectorAll(this.cellSelector))
        : Array.from(this.children);
    }

    select(index) {
      var cells = this.cells;
      if (cells.length === 0) return;

      this.selectedIndex = (index + cells.length) % cells.length;

      cells.forEach(
        function (cell, position) {
          cell.classList.toggle('is-selected', position === this.selectedIndex);
        }.bind(this)
      );

      this.dispatchEvent(new CustomEvent('carousel:change', { bubbles: true, detail: { index: this.selectedIndex } }));
    }

    next() {
      this.select(this.selectedIndex + 1);
    }

    previous() {
      this.select(this.selectedIndex - 1);
    }

    startAutoplay() {
      var seconds = parseFloat(this.getAttribute('autoplay'));
      if (!seconds || this.cells.length < 2) return;

      this.timer = window.setInterval(this.next.bind(this), seconds * 1000);

      this.addEventListener(
        'pointerenter',
        function () {
          window.clearInterval(this.timer);
        }.bind(this)
      );

      this.addEventListener(
        'pointerleave',
        function () {
          this.timer = window.setInterval(this.next.bind(this), seconds * 1000);
        }.bind(this)
      );
    }

    bindSwipe() {
      if (!this.hasAttribute('allow-swipe')) return;

      var startX = null;

      this.addEventListener(
        'pointerdown',
        function (event) {
          startX = event.clientX;
        },
        { passive: true }
      );

      this.addEventListener(
        'pointerup',
        function (event) {
          if (startX === null) return;
          var delta = event.clientX - startX;
          startX = null;
          if (Math.abs(delta) < 40) return;
          if (delta < 0) {
            this.next();
          } else {
            this.previous();
          }
        }.bind(this),
        { passive: true }
      );
    }
  };

  /* Each tag needs its own constructor — customElements.define rejects a
     constructor that is already registered. */
  define('slideshow-carousel', class extends FadeCarousel {});
  define('press-carousel', class extends FadeCarousel {});

  /* ========================================================= product gallery */
  define(
    'custom-cursor',
    class extends HTMLElement {
      connectedCallback() {
        this.parent = this.parentElement;
        if (!this.parent || !media('screen and (pointer: fine)')) return;

        this.parent.addEventListener('pointermove', this.onMove.bind(this));
        this.parent.addEventListener('pointerleave', this.onLeave.bind(this));
      }

      onMove(event) {
        var rect = this.parent.getBoundingClientRect();
        var x = event.clientX - rect.left;
        var y = event.clientY - rect.top;

        this.style.translate = Math.round(x - this.offsetWidth / 2) + 'px ' + Math.round(y - this.offsetHeight / 2) + 'px';
        this.classList.add('is-visible');
        this.classList.toggle('is-half-start', x < rect.width / 2);
        this.classList.toggle('is-half-end', x >= rect.width / 2);
      }

      onLeave() {
        this.classList.remove('is-visible');
      }
    }
  );

  define(
    'product-gallery',
    class extends HTMLElement {
      connectedCallback() {
        this.carousel = this.querySelector('.product-gallery__carousel');
        if (!this.carousel) return;

        this.carousel.addEventListener('click', this.onClick.bind(this));
      }

      onClick(event) {
        /* Clicking the right half advances, the left half goes back — the arrow
           cursor shows which one is about to happen. */
        if (event.target.closest('button, a[href]')) return;
        if (!media('screen and (pointer: fine)')) return;

        var rect = this.carousel.getBoundingClientRect();
        if (event.clientX - rect.left > rect.width / 2) {
          this.carousel.next();
        } else {
          this.carousel.previous();
        }
      }
    }
  );

  /* ============================================================ small pieces */
  define(
    'quantity-selector',
    class extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input');
        var buttons = this.querySelectorAll('button');
        if (!this.input || buttons.length < 2) return;

        this.decrease = buttons[0];
        this.increase = buttons[buttons.length - 1];

        this.decrease.addEventListener('click', this.step.bind(this, -1));
        this.increase.addEventListener('click', this.step.bind(this, 1));
        this.input.addEventListener('change', this.sync.bind(this));
        this.sync();
      }

      step(amount) {
        var min = parseInt(this.input.min, 10) || 0;
        this.input.value = Math.max(min, (parseInt(this.input.value, 10) || 0) + amount);
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      sync() {
        var min = parseInt(this.input.min, 10) || 0;
        this.decrease.disabled = (parseInt(this.input.value, 10) || 0) <= min;
        this.style.setProperty('--quantity-selector-character-count', String(this.input.value).length + 'ch');
      }
    }
  );

  define(
    'safe-sticky',
    class extends HTMLElement {
      connectedCallback() {
        this.style.position = 'sticky';
        this.style.top = 'calc(var(--header-height, 0px) + var(--sticky-offset, 1rem))';
      }
    }
  );

  define(
    'split-lines',
    class extends HTMLElement {
      connectedCallback() {
        if (this.dataset.split === 'true') return;
        this.dataset.split = 'true';

        var words = this.textContent.trim().split(/\s+/);
        this.textContent = '';

        words.forEach(
          function (word, index) {
            var span = document.createElement('span');
            span.className = 'word';
            span.textContent = index === words.length - 1 ? word : word + ' ';
            this.append(span);
          }.bind(this)
        );
      }
    }
  );

  define(
    'reading-text',
    class extends HTMLElement {
      connectedCallback() {
        this.words = Array.from(this.querySelectorAll('.word'));
        if (this.words.length === 0) return;

        /* How far the page has to scroll for the text to be read through, expressed the
           way the reference does: so much viewport height per character. */
        var speed = parseFloat(this.getAttribute('reading-speed')) || 0.3;
        var characters = this.words.reduce(function (total, word) {
          return total + word.textContent.length;
        }, 0);
        this.style.setProperty('--reading-text-scroll-height', speed * characters + 'vh');

        this.onScroll = this.update.bind(this);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll);
        this.update();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }

      update() {
        var rect = this.getBoundingClientRect();
        /* The runway is everything the block has beyond one screenful: while it is being
           consumed the inner panel is pinned and the words light up one by one. */
        var runway = rect.height - window.innerHeight;
        var progress = runway > 0 ? -rect.top / runway : (rect.top < window.innerHeight ? 1 : 0);
        var readable = Math.round(this.words.length * Math.max(0, Math.min(progress, 1)));

        this.words.forEach(function (word, index) {
          word.classList.toggle('is-read', index < readable);
        });
      }
    }
  );

  define(
    'x-toast',
    class extends HTMLElement {
      show(message) {
        this.textContent = message;
        this.setAttribute('open', '');
        window.clearTimeout(this.timer);
        this.timer = window.setTimeout(
          function () {
            this.removeAttribute('open');
          }.bind(this),
          3500
        );
      }
    }
  );

  define(
    'product-recommendations',
    class extends HTMLElement {
      connectedCallback() {
        if (this.dataset.loaded === 'true') return;

        var productId = this.getAttribute('product-id');
        var limit = this.getAttribute('limit') || 5;
        var intent = this.getAttribute('intent') || 'related';
        // the template's own section id, so the response carries this section's settings
        // (a bare section type renders with schema defaults, i.e. the English title)
        var sectionId = this.getAttribute('section-id') || 'product-recommendations';
        if (!productId) return;

        var root = (window.themeVariables && window.themeVariables.settings.routes.root) || '/';
        var url =
          root.replace(/\/$/, '') +
          '/recommendations/products?section_id=' +
          encodeURIComponent(sectionId) +
          '&product_id=' +
          productId +
          '&limit=' +
          limit +
          '&intent=' +
          intent;

        fetch(url)
          .then(function (response) {
            return response.text();
          })
          .then(
            function (html) {
              var parsed = new DOMParser().parseFromString(html, 'text/html');
              var fresh = parsed.querySelector('product-recommendations');
              if (fresh) this.innerHTML = fresh.innerHTML;
              this.dataset.loaded = 'true';
            }.bind(this)
          )
          .catch(function () {});
      }
    }
  );

  /* Elements that only need to exist so the markup validates and CSS can hook in. */
  ['button-content', 'product-card', 'product-form', 'loading-bar', 'cart-count', 'highlighted-heading',
   'accordion-disclosure', 'variant-picker', 'media-block', 'badge-list', 'price-list', 'sale-price',
   'compare-at-price', 'unit-price', 'facet-filters'].forEach(function (name) {
    define(name, class extends HTMLElement {});
  });
})();
